import { getFormattedDisplayNameForEqlField } from '@modules/common/components/EqlFieldsAutoComplete/helpers';
import {
  useIsUnifiedFieldsEnabled,
  useShouldRemoveFileUploadPrefix,
} from '@modules/common/hooks';
import { useIsSingleRecordQuery } from '@modules/quantify/hooks';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { Typography } from '@mui/material';
import { capitalize } from 'lodash';

interface MeanFormulaDefinitionParams {
  sum: number;
}

export function SumFormulaDefinition({ sum }: MeanFormulaDefinitionParams) {
  const groupBy = useQuantifyStore(({ query }) => query.groupBy);
  const measuredAsCriteria = useQuantifyStore(
    ({ query }) => query.measuredAsCriteria,
  );
  const isSingleRecordQuery = useIsSingleRecordQuery();
  const shouldRemoveFileUploadPrefix = useShouldRemoveFileUploadPrefix();
  const isUnifiedFieldEnabled = useIsUnifiedFieldsEnabled();

  if (isSingleRecordQuery) {
    return (
      <Typography variant="caption">
        {`Weighted Sum: `}
        <Typography component="span" variant="inherit" fontWeight="bold">
          {sum}
        </Typography>
        {` derived from multiplying each `}
        <Typography component="span" variant="inherit" fontWeight="bold">
          {getFormattedDisplayNameForEqlField({
            eqlField: groupBy,
            shouldRemoveFileUploadPrefix,
            isUnifiedFieldEnabled,
          })}
        </Typography>
        {` value by the number of times it occurs, then summing those products together.`}
      </Typography>
    );
  }

  const objectDisplayName = capitalize(measuredAsCriteria?.objectType);

  return (
    <Typography variant="caption">
      {`Sum: `}
      <Typography component="span" variant="inherit" fontWeight="bold">
        {sum}
      </Typography>
      {` is derived by adding the `}
      <Typography component="span" variant="inherit" fontWeight="bold">
        {measuredAsCriteria?.displayName}
      </Typography>
      {` for each matching `}
      <Typography component="span" variant="inherit" fontWeight="bold">
        {objectDisplayName}
      </Typography>
      {`. `}
      <Typography component="span" variant="inherit" fontWeight="bold">
        {measuredAsCriteria?.displayName}
      </Typography>
      {` from ${objectDisplayName.toLowerCase().startsWith('a') ? 'an' : 'a'} `}
      <Typography component="span" variant="inherit" fontWeight="bold">
        {objectDisplayName}
      </Typography>
      {` is only counted once while calculating sum.`}
    </Typography>
  );
}
